/**
 * "Which meal is the user about to cook?" — the anchor the week list scrolls to
 * on open (#579).
 *
 * The list used to park on today's *day header*, which is only the right answer
 * when today's first slot holds the next dish. On a phone the visible band under
 * the header/grocery/progress chrome is barely one card tall, so a day whose
 * déjeuner and dîner are empty spends that whole band on two "Ajouter…" rows and
 * pushes tonight's souper off the bottom edge. Anchoring on the meal instead of
 * the day fixes that without moving a single pixel of layout.
 *
 * Kept in a `.ts` (not the `.tsx` that uses it) so Jest can import it — the
 * project's tsconfig emits `jsx: preserve`, which makes `.tsx` unimportable
 * from tests.
 */

import type { PlannedMeal } from '../../../redux/mealPlanSlice';
import { isEntryMeal } from '../../../redux/mealEntry';
import { slotKey } from './constants';
import { isAccepted } from './slotMeals';
import { todayIso, minutesIntoDay, dayIndexInWeek } from '../../../utils/localDate';

export { dayIndexInWeek };

export interface NextMealTarget {
  /** slotKey (mi * 7 + di) of the slot holding the next dish. */
  sk: number;
  /** Day index, Monday = 0. */
  di: number;
  /** Meal index — 0 breakfast, 1 lunch, 2 dinner. */
  mi: number;
  /** The specific dish, so callers can highlight or log it. */
  meal: PlannedMeal;
}

/**
 * Minutes-past-midnight after which a slot stops being "the next meal to cook"
 * today. A dish nobody marked cooked shouldn't out-rank tonight's dinner just
 * because breakfast sits higher in the list — at 17:37 the answer is souper.
 * Later days ignore these entirely: tomorrow's breakfast is still ahead of you.
 */
const SLOT_CUTOFF_MINUTES = [10 * 60, 15 * 60, Infinity];

/**
 * The clock read every caller of `findNextMealTarget` needs: which calendar day
 * the user is living in, and how far into it they are.
 *
 * Shared rather than inlined because it was inlined three times — in the mobile
 * hero, the mobile day list and the desktop bar — and all three read the day in
 * UTC. Past 20:00 in Montreal that is tomorrow, so the hero labelled tomorrow's
 * dinner "Aujourd'hui · Souper" (#679).
 */
export function nowAnchor(now: Date = new Date()): { today: string; nowMinutes: number } {
  return { today: todayIso(now), nowMinutes: minutesIntoDay(now) };
}

interface FindArgs {
  slotMeals: Map<number, PlannedMeal[]>;
  weekStart: string;
  /** Today as YYYY-MM-DD. */
  today: string;
  /** Minutes past midnight, local time. */
  nowMinutes: number;
  /**
   * Whether a recipeId resolves to something the list can actually draw. A slot
   * whose dishes are all unresolvable renders as an empty "Ajouter…" row, so
   * scrolling to it would land on nothing.
   */
  resolves: (recipeId: string) => boolean;
}

/**
 * The first dish from today onward that still needs cooking, scanning day by day
 * and, within a day, breakfast → lunch → dinner.
 *
 * Returns null when there is nothing to anchor on: today is outside this week
 * (the user is browsing another week), or every remaining dish is already
 * cooked. Callers should fall back to their previous behaviour.
 */
export function findNextMealTarget({
  slotMeals,
  weekStart,
  today,
  nowMinutes,
  resolves,
}: FindArgs): NextMealTarget | null {
  const todayDi = dayIndexInWeek(weekStart, today);
  if (todayDi === null) return null;

  for (let di = todayDi; di < 7; di++) {
    for (let mi = 0; mi < 3; mi++) {
      if (di === todayDi && nowMinutes >= SLOT_CUTOFF_MINUTES[mi]) continue;

      const sk = slotKey(mi, di);
      const dishes = slotMeals.get(sk);
      if (!dishes || dishes.length === 0) continue;

      // Restaurant / restes / note / "rien" aren't cooking, and neither is a
      // dish whose recipe the index can't resolve — the row wouldn't render.
      // A side next to a note is not the meal either: the note is the main,
      // and its row in the list draws the whole slot.
      if (dishes.some(isEntryMeal)) continue;
      // A proposal is a suggestion, not the dinner: once the accepted dish was
      // cooked, the slot's proposal used to become « Aujourd'hui · souper » and
      // the cooked dinner its side.
      const next = dishes.find(
        (m) => !isEntryMeal(m) && isAccepted(m) && m.status !== 'cooked' && resolves(m.recipeId),
      );
      if (next) return { sk, di, mi, meal: next };
    }
  }

  return null;
}

/**
 * The dish the phone's hero card shows: the next one still ahead *today*, or
 * nothing.
 *
 * The hero used to take `findNextMealTarget`'s answer whatever day it fell on,
 * and the list drops the slot the hero shows (`isHeroOnlySlot`) so the dish is
 * not drawn twice. With nothing tonight and Thursday's dinner planned, the hero
 * read « Demain · Souper » and Thursday vanished from the list, with « Planifier
 * les 7 jours suivants » in its place (#828; a Thursday lunch the same way,
 * #830). With no hero, tonight's empty « Ajouter un souper » is the first thing
 * on the page, which is the gap worth showing.
 */
export function findHeroTarget(args: FindArgs): NextMealTarget | null {
  const target = findNextMealTarget(args);
  if (!target) return null;
  return target.di === dayIndexInWeek(args.weekStart, args.today) ? target : null;
}

/**
 * The next planned meal that is not a recipe: a simple meal, a restaurant,
 * leftovers — from today onward, in the same order as `findNextMealTarget`.
 *
 * `findNextMealTarget` looks for something to *cook*, so it walks past these —
 * and the desktop bar, which shows its answer, jumped past them. A cook who
 * had just replaced Thursday's salad with « Dumplings et légumes » saw the bar
 * announce Saturday's breakfast, and wrote to ask whether she had done the
 * right thing (8 Oct 2026). Looking at today alone was not enough: with
 * nothing tonight and « Sue's » on Friday — her own week — the bar still
 * skipped Friday for Saturday. « Rien » is not a plan for the evening, so it is
 * passed over.
 */
export function findNextEntryTarget({
  slotMeals,
  weekStart,
  today,
  nowMinutes,
}: Omit<FindArgs, 'resolves'>): NextMealTarget | null {
  const todayDi = dayIndexInWeek(weekStart, today);
  if (todayDi === null) return null;

  for (let di = todayDi; di < 7; di++) {
    for (let mi = 0; mi < 3; mi++) {
      if (di === todayDi && nowMinutes >= SLOT_CUTOFF_MINUTES[mi]) continue;
      const sk = slotKey(mi, di);
      const entry = slotMeals.get(sk)?.find(
        (m) => isEntryMeal(m) && m.entry!.type !== 'skip' && m.status !== 'cooked',
      );
      if (entry) return { sk, di, mi, meal: entry };
    }
  }
  return null;
}

export type BarTarget =
  | { kind: 'recipe'; target: NextMealTarget }
  | { kind: 'entry'; target: NextMealTarget };

/**
 * What the desktop bar answers with: whichever comes first, the next recipe to
 * cook or the next non-recipe plan. The two never share a slot — the recipe
 * search skips a slot holding an entry.
 */
export function pickBarTarget(
  recipe: NextMealTarget | null,
  entry: NextMealTarget | null,
): BarTarget | null {
  if (!entry) return recipe ? { kind: 'recipe', target: recipe } : null;
  if (recipe && (recipe.di < entry.di || (recipe.di === entry.di && recipe.mi < entry.mi))) {
    return { kind: 'recipe', target: recipe };
  }
  return { kind: 'entry', target: entry };
}
